import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Music, Calendar, Users, LogOut, ExternalLink } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, loading, logout } = useAuth();
  const [permissions, setPermissions] = useState({ can_manage_users: false, role: 'none' });

  useEffect(() => {
    if (!loading && !user) {
      navigate('/admin/login');
    } else if (user) {
      fetchPermissions();
    }
  }, [user, loading, navigate]);

  const fetchPermissions = async () => {
    try {
      const response = await axios.get(`${API}/auth/permissions`, { withCredentials: true });
      setPermissions(response.data);
    } catch (error) {
      console.error('Error fetching permissions:', error);
    }
  };

  const handleLogout = async () => {
    await logout();
    toast.success('Logged out successfully');
    navigate('/');
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="admin-container">
      {/* Header */}
      <header className="admin-header">
        <div className="admin-header-content">
          <div className="admin-logo-section" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
            <img 
              src="https://customer-assets.emergentagent.com/job_lethbridge-angels/artifacts/znfka10j_RA-blacktransparent%20cropped%20no%20vol.png" 
              alt="Rebel Angels" 
              className="admin-brand-logo"
            />
          </div>
          <div className="admin-user-section">
            <img src={user.picture} alt={user.name} className="admin-user-avatar" />
            <span className="admin-user-name">{user.name}</span>
            <Button variant="outline" className="admin-logout-button" onClick={handleLogout}>
              <LogOut className="button-icon" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="admin-main">
        <div className="admin-content">
          <h1 className="admin-page-title">Admin Dashboard</h1>
          <p className="admin-page-subtitle">Manage your shows and band members</p>

          <div className="admin-cards-grid">
            <Card className="admin-dashboard-card" onClick={() => navigate('/admin/shows')} style={{ cursor: 'pointer' }}>
              <CardContent className="admin-card-content">
                <div className="admin-card-icon-wrapper">
                  <Calendar className="admin-card-icon" />
                </div>
                <h2 className="admin-card-title">Manage Shows</h2>
                <p className="admin-card-description">
                  Add, edit, and delete upcoming shows and events
                </p>
              </CardContent>
            </Card>

            <Card className="admin-dashboard-card" onClick={() => navigate('/admin/members')} style={{ cursor: 'pointer' }}>
              <CardContent className="admin-card-content">
                <div className="admin-card-icon-wrapper">
                  <Users className="admin-card-icon" />
                </div>
                <h2 className="admin-card-title">Manage Band Members</h2>
                <p className="admin-card-description">
                  Update band member information and bios
                </p>
              </CardContent>
            </Card>

            <Card className="admin-dashboard-card" onClick={() => navigate('/admin/gallery')} style={{ cursor: 'pointer' }}>
              <CardContent className="admin-card-content">
                <div className="admin-card-icon-wrapper">
                  <Music className="admin-card-icon" />
                </div>
                <h2 className="admin-card-title">Manage Gallery</h2>
                <p className="admin-card-description">
                  Add, edit, and organize gallery photos
                </p>
              </CardContent>
            </Card>

            <Card className="admin-dashboard-card" onClick={() => navigate('/admin/social-media')} style={{ cursor: 'pointer' }}>
              <CardContent className="admin-card-content">
                <div className="admin-card-icon-wrapper">
                  <ExternalLink className="admin-card-icon" />
                </div>
                <h2 className="admin-card-title">Social Media Links</h2>
                <p className="admin-card-description">
                  Update Facebook, Instagram, and YouTube links
                </p>
              </CardContent>
            </Card>

            {permissions.can_manage_users && (
              <Card className="admin-dashboard-card" onClick={() => navigate('/admin/users')} style={{ cursor: 'pointer' }}>
                <CardContent className="admin-card-content">
                  <div className="admin-card-icon-wrapper">
                    <Users className="admin-card-icon" />
                  </div>
                  <h2 className="admin-card-title">Authorized Users</h2>
                  <p className="admin-card-description">
                    Manage who can access the admin panel
                  </p>
                </CardContent>
              </Card>
            )}

            {permissions.role === "primary_admin" && (
              <Card className="admin-dashboard-card" onClick={() => navigate('/admin/sessions')} style={{ cursor: 'pointer' }}>
                <CardContent className="admin-card-content">
                  <div className="admin-card-icon-wrapper">
                    <Users className="admin-card-icon" />
                  </div>
                  <h2 className="admin-card-title">Active Sessions</h2>
                  <p className="admin-card-description">
                    View and manage currently logged-in users
                  </p>
                </CardContent>
              </Card>
            )}
          </div>

          <div className="admin-quick-actions">
            <Button className="admin-action-button" onClick={() => navigate('/')}>
              View Website
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
